import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, FileText } from "lucide-react";
import { getImageUrl } from "@/lib/api";

/**
 * File upload field. accept prop controls file type (image/* or application/pdf).
 * Stores URL string in value/onChange.
 */
export default function FileUpload({ value, onChange, accept = "image/*", label = "Upload" }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("File terunggah");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const isImage = accept?.startsWith("image");
  const isPdf = value && value.toLowerCase().endsWith(".pdf");

  return (
    <div className="space-y-2">
      {value && (
        <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-slate-50 max-w-sm">
          {isImage && !isPdf ? (
            <img src={getImageUrl(value)} alt="preview" className="w-full h-40 object-cover" />
          ) : (
            <div className="p-4 flex items-center gap-3">
              <FileText className="h-6 w-6 text-[var(--brand-blue)]" />
              <a href={value} target="_blank" rel="noreferrer" className="text-xs text-slate-700 truncate flex-1 hover:text-[var(--brand-blue)]">{value}</a>
            </div>
          )}
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white shadow-md text-slate-700 hover:text-red-500 flex items-center justify-center"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 hover:border-[var(--brand-blue)] px-4 py-2.5 text-sm font-semibold text-slate-700 hover:text-[var(--brand-blue)] transition"
          data-testid="file-upload-btn"
        >
          <Upload className="h-4 w-4" /> {uploading ? "Mengunggah..." : label}
        </button>
        <input
          ref={ref}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <span className="text-xs text-slate-500">atau tempel URL di kolom bawah</span>
      </div>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://..."
        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]"
      />
    </div>
  );
}
